import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { copyText } from "@/lib/clipboard";
import { Ticket, Copy, RefreshCw, Loader2, Printer, Check, KeyRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { RARITY_NEON } from "@/lib/cardPdf";
import { cardImageStyle, CardHoverPreview } from "@/components/Card3D";
import PrintSheetDialog from "./PrintSheetDialog";

export function EditionCodesDialog({ template }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({}); // edition_number -> card obj
  const [printOpen, setPrintOpen] = useState(false);
  const tid = template.id;

  const { data, isLoading } = useQuery({
    queryKey: ["edition-codes", tid],
    queryFn: async () => (await api.get(`/admin/templates/${tid}/edition-codes`)).data,
    enabled: open,
  });

  const gen = useMutation({
    mutationFn: async (body) => (await api.post(`/admin/templates/${tid}/edition-codes`, body)).data,
    onSuccess: (d) => {
      qc.setQueryData(["edition-codes", tid], d);
      qc.invalidateQueries({ queryKey: ["admin-codes"] });
      if (typeof d.created === "number") toast.success(d.created > 0 ? `Generated ${d.created} code${d.created === 1 ? "" : "s"}` : "All editions already have codes");
    },
    onError: (e) => toast.error(apiError(e, "Failed to generate")),
  });

  const editions = data?.editions || [];
  const printable = (e) => e.status === "printed";
  const cardObj = (e) => ({
    id: `${tid}-${e.edition_number}`,
    front_image: e.front_image,
    orientation: e.orientation,
    image_fit: e.image_fit,
    image_scale: e.image_scale,
    image_pos_x: e.image_pos_x,
    image_pos_y: e.image_pos_y,
    title: e.title,
    rarity: e.rarity,
    code: e.code,
    edition_number: e.edition_number,
    edition_count: e.edition_count,
  });

  const toggle = (e) => setSelected((m) => {
    const next = { ...m };
    if (next[e.edition_number]) delete next[e.edition_number];
    else next[e.edition_number] = cardObj(e);
    return next;
  });
  const selectAllWithCodes = () => {
    const m = {};
    editions.filter(printable).forEach((e) => { m[e.edition_number] = cardObj(e); });
    setSelected(m);
  };
  const selectedList = Object.values(selected);

  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "numeric" }); } catch { return null; }
  };
  const statusBadge = (e) => {
    if (e.status === "claimed") return <span className="text-[10px] uppercase tracking-wider text-emerald-300">Claimed · {e.redeemed_by || e.owner_name}{fmtDate(e.redeemed_at) ? ` · ${fmtDate(e.redeemed_at)}` : ""}</span>;
    if (e.status === "printed") return <span className="text-[10px] uppercase tracking-wider text-cyan-300">Printed · not owned yet{fmtDate(e.code_created_at) ? ` · ${fmtDate(e.code_created_at)}` : ""}</span>;
    if (e.status === "minted") return <span className="text-[10px] uppercase tracking-wider text-amber-300">Owned · {e.owner_name}</span>;
    return <span className="text-[10px] uppercase tracking-wider text-white/40">Available</span>;
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        data-testid={`edition-codes-button-${tid}`}
        onClick={() => setOpen(true)}
        title="Edition claim codes"
        className="h-7 w-7 p-0 text-emerald-300/80 hover:text-emerald-200"
      >
        <Ticket className="h-4 w-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edition-codes-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-[0.08em] flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-emerald-300" /> EDITION CODES — {template.title}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid="gen-all-codes" onClick={() => gen.mutate({})} disabled={gen.isPending} className="bg-emerald-400 text-black hover:bg-emerald-300">
              {gen.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Ticket className="h-4 w-4 mr-1" />} Generate all missing
            </Button>
            <Button data-testid="regen-all-codes" variant="outline" onClick={() => { if (window.confirm("Regenerate all un-redeemed codes? Existing printed codes will stop working.")) gen.mutate({ regenerate: true }); }} disabled={gen.isPending} className="border-white/15 text-white">
              <RefreshCw className="h-4 w-4 mr-1" /> Regenerate
            </Button>
            <div className="flex-1" />
            <Button data-testid="select-all-coded" variant="ghost" onClick={selectAllWithCodes} className="text-white/70 hover:text-white h-8">Select all coded</Button>
            <Button data-testid="print-editions" onClick={() => setPrintOpen(true)} disabled={selectedList.length === 0} className="bg-cyan-400 text-black hover:bg-cyan-300 h-8">
              <Printer className="h-4 w-4 mr-1" /> Print ({selectedList.length})
            </Button>
          </div>

          {isLoading ? (
            <div className="py-10 text-center text-white/40"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-1.5 mt-1">
              {editions.map((e) => {
                const isSel = !!selected[e.edition_number];
                const neon = RARITY_NEON[e.rarity] || "#fff";
                return (
                  <div key={e.edition_number} className={cn("flex items-center gap-3 rounded-lg px-3 py-2 hud-pill", isSel && "ring-1 ring-cyan-400")} data-testid={`edition-row-${e.edition_number}`}>
                    <button
                      type="button"
                      onClick={() => printable(e) && toggle(e)}
                      disabled={!printable(e)}
                      data-testid={`edition-select-${e.edition_number}`}
                      title={e.status === "minted" ? "Already minted — can't print" : (e.code ? "Select for print" : "Generate a code first")}
                      className={cn("h-5 w-5 rounded flex items-center justify-center border shrink-0", printable(e) ? (isSel ? "bg-cyan-400 border-cyan-300 text-black" : "border-white/30 hover:border-white") : "border-white/10 opacity-30 cursor-not-allowed")}
                    >
                      {isSel && <Check className="h-3.5 w-3.5" />}
                    </button>
                    <CardHoverPreview card={cardObj(e)} side="right">
                      <div className={cn("relative shrink-0 rounded overflow-hidden border border-white/10 cursor-help", e.orientation === "landscape" ? "w-12 h-9" : "w-9 h-12")} data-testid={`edition-thumb-${e.edition_number}`}>
                        {e.front_image
                          ? <img src={e.front_image} alt="" className="absolute inset-0 w-full h-full" style={cardImageStyle(e)} />
                          : <div className="absolute inset-0 bg-[hsl(220_18%_14%)]" />}
                      </div>
                    </CardHoverPreview>
                    <div className="font-mono text-sm font-bold w-12 shrink-0" style={{ color: neon }}>{e.edition_number}/{e.edition_count}</div>
                    <div className="flex-1 min-w-0">
                      {e.code ? (
                        <button onClick={async () => { (await copyText(e.code)) ? toast.success("Copied") : toast.error("Couldn't copy"); }} data-testid={`copy-edition-code-${e.edition_number}`} className="inline-flex items-center gap-1.5 font-mono text-sm text-white hover:text-cyan-300 tracking-wider">
                          {e.code} <Copy className="h-3 w-3 text-white/40" />
                        </button>
                      ) : (
                        <span className="text-white/30 text-xs italic">No code yet</span>
                      )}
                      <div>{statusBadge(e)}</div>
                    </div>
                    {!e.code && (
                      <Button size="sm" variant="outline" data-testid={`gen-edition-${e.edition_number}`} onClick={() => gen.mutate({ edition_number: e.edition_number })} disabled={gen.isPending} className="border-white/15 text-white h-7 shrink-0">
                        Generate
                      </Button>
                    )}
                  </div>
                );
              })}
              {data?.truncated && <div className="text-[10px] text-amber-300/70 text-center pt-2">Showing first 600 editions.</div>}
            </div>
          )}
          <div className="text-[10px] text-white/40 mt-1">Each code claims its exact edition when redeemed on the Redeem page. Print the cards, then cover each code with a scratch-off sticker.</div>
        </DialogContent>
      </Dialog>

      <PrintSheetDialog open={printOpen} onOpenChange={setPrintOpen} cards={selectedList} />
    </>
  );
}
